import { PostReferralConsent, ReferralUpdate } from '@packages/core-shared';
import { getFetchResponseErrorMessage } from '../../helpers';
import { ApiRepository } from '../../helpers/ApiRepository';

export class ReferralsApiRepository extends ApiRepository {
  /**
   * Sends a referral from a referral site. The site's signed-in referrer is
   * who `crm_referrer` is checked against - the public Vitality form has no
   * session and posts to the endpoint directly.
   */
  async create(referral: Record<string, unknown>) {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/referrals`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(referral),
      },
    );

    if (!response.ok) {
      throw new Error(await getFetchResponseErrorMessage(response));
    }
  }

  async generateId() {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/generate-id`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
      },
    );

    if (!response.ok) {
      const errorMsg = await getFetchResponseErrorMessage(response);
      throw new Error(errorMsg);
    }

    const body = (await response.json()) as { referralId?: string };
    if (!body.referralId) {
      throw new Error('The API did not return a referral ID');
    }

    return body.referralId;
  }

  async generateDownloadUrl({
    referralId,
    filePath,
  }: {
    referralId: string;
    filePath: string;
  }) {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/${referralId}/storage/url`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ path: filePath }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to generate download url for ${filePath}`);
    }

    const json = await response.json();

    return json?.url;
  }

  // Shares one document of a referral with the MDT — any type (PPL-3549).
  // The storage path identifies it: two documents can share a name.
  async uploadReferralDocumentToPlatform({
    referralId,
    path,
  }: {
    referralId: string;
    path: string;
  }) {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }

    return await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/${referralId}/documents/platform`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ path }),
      },
    );
  }

  async update(referralId: string, body: Partial<ReferralUpdate>) {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/${referralId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        method: 'PUT',
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      const errorMsg = await getFetchResponseErrorMessage(response);
      throw new Error(errorMsg);
    }
  }

  async getDetails(referralId: string) {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/${referralId}/details`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        method: 'GET',
      },
    );

    if (response.ok) {
      const data = await response.json();
      return data?.details ?? '';
    } else {
      return 'Failed to load';
    }
  }

  async cancel(referralId: string, body: { cancelReasonKey: string }) {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/${referralId}/cancel`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      const errorMsg = await getFetchResponseErrorMessage(response);
      throw new Error(errorMsg);
    }
  }

  // Record what the patient said when asked for their consent by telephone
  // (requested referrals)
  async postConsent(referralId: string, body: PostReferralConsent) {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/${referralId}/consent`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      const errorMsg = await getFetchResponseErrorMessage(response);
      throw new Error(errorMsg);
    }
  }

  async pause(referralId: string) {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/${referralId}/pause`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
      },
    );

    if (!response.ok) {
      const errorMsg = await getFetchResponseErrorMessage(response);
      throw new Error(errorMsg);
    }
  }
}
